'use client'

import { ArrowUpDown, Boxes, Check, ChevronDown, FileCode, Github, History, ListFilter, Lock, Plus, Search, Send, Tags, User } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ViewModeToggle } from '@/components/view-mode-toggle'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/utils/api-error-handler'
import { appendWorkspaceScopeToPath, normalizeTeamId } from '@/utils/workspace-scope'

type AppCatalogItem = {
  app_key: string
  name: string
  version: string | null
  avatar_url: string | null
  overview: string | null
  authors: string[] | null
  publication_date: string | null
  last_updated_at: string | null
  paper_link: string | null
  github_link: string | null
  category: string | null
  kind: 'model' | 'workflow' | 'tool'
  search_tags: string[] | null
  is_active: boolean
  sort_order: number
  feature_key: string | null
  has_access: boolean
  on_waitlist: boolean
  launch_type: 'route' | 'workflow_template'
  launch_target: string
}

type AppCatalogListResponse = {
  data: AppCatalogItem[]
  total?: number | null
  next_cursor?: string | null
}

const kindLabel: Record<string, string> = {
  model: 'Model',
  workflow: 'Workflow',
  tool: 'Tool',
}

const KIND_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'model', label: 'Models' },
  { value: 'workflow', label: 'Workflows' },
  { value: 'tool', label: 'Tools' },
] as const

const SORT_FILTER_OPTIONS = [
  { value: 'alphabetical', label: 'Alphabetical' },
  { value: 'last_updated', label: 'Last updated' },
] as const

const FEATURED_APP_KEYS = ['esmfold2', 'openfold-3', 'openmmdl'] as const

const FEATURED_COVER_BY_APP_KEY: Record<string, string> = {
  'openfold-3': '/news/openfold-3-promo-2.png',
  openmmdl: '/news/openMMDL.png',
  esmfold2: '/news/Esmfold2-demo.png',
  openmm: '/news/md.gif',
  boltzgen: '/news/workflows-preview.png',
}

const NEW_APP_KEYS = new Set(['openfold-3', 'openmmdl', 'esmfold2', 'nesso'])

const DARK_INVERT_APP_KEYS = new Set(['agent-cli', 'code-viewer', 'boltzgen'])

const APP_KEY_TO_MODEL: Record<string, string> = {
  'alphafold-2': 'monomer',
  'boltz-1': 'boltz',
  'boltz-2': 'boltz-2',
  'simple-fold': 'simplefold_100M',
  'esm-1b': 'esm1b',
  'openfold-3': 'openfold3',
  'chai-1': 'chai1',
  'intellifold': 'intellifold',
  nesso: 'nesso',
  esmfold2: 'esmfold2-fast-2026-05',
}

const EXTERNAL_APP_LINK_BY_KEY: Record<string, string> = {
  'agent-cli': 'https://docs.fastfold.ai/agents/cli',
}

function AppAvatar({ app, sizeClass }: { app: AppCatalogItem; sizeClass: string }) {
  const needsInvert = DARK_INVERT_APP_KEYS.has(app.app_key)
  return app.avatar_url ? (
    <div className={`${sizeClass} overflow-hidden bg-muted/60`}>
      <Image
        src={app.avatar_url}
        alt={app.name}
        width={48}
        height={48}
        className={`h-full w-full object-cover${needsInvert ? ' dark:invert' : ''}`}
      />
    </div>
  ) : (
    <div className={`${sizeClass} flex items-center justify-center bg-muted/60`}>
      <FileCode className="h-1/2 w-1/2 text-muted-foreground" />
    </div>
  )
}

function AppsBadge({ className, ...props }: React.ComponentProps<typeof Badge>) {
  return <Badge className={cn(className, 'border-0 font-normal shadow-none')} {...props} />
}

export default function AppsCatalogPage() {
  const PAGE_SIZE = 24
  const [loadedFeaturedCovers, setLoadedFeaturedCovers] = React.useState<Record<string, boolean>>({})
  const [loading, setLoading] = React.useState(true)
  const [isFetching, setIsFetching] = React.useState(false)
  const [apps, setApps] = React.useState<AppCatalogItem[]>([])
  const [totalCount, setTotalCount] = React.useState<number | null>(0)
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [selectedKind, setSelectedKind] = React.useState('all')
  const [selectedCategory, setSelectedCategory] = React.useState('all')
  const [selectedSort, setSelectedSort] = React.useState<'alphabetical' | 'last_updated'>('last_updated')
  const [categoryOptions, setCategoryOptions] = React.useState<string[]>([])
  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>('grid')
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [launchingAppKey, setLaunchingAppKey] = React.useState<string | null>(null)
  const searchWrapRef = React.useRef<HTMLDivElement | null>(null)
  const hasLoadedOnceRef = React.useRef(false)
  const requestSeqRef = React.useRef(0)

  const [requestDialogApp, setRequestDialogApp] = React.useState<AppCatalogItem | null>(null)
  const [requestUseCase, setRequestUseCase] = React.useState('')
  const [requestSubmitting, setRequestSubmitting] = React.useState(false)
  const [requestedKeys, setRequestedKeys] = React.useState<Set<string>>(new Set())

  const { user: authUser, isLoading: isAuthLoading } = useAuth()
  const isPublicMode = !isAuthLoading && !authUser
  const { activeTeamId, setActiveTeamId } = useWorkspace()
  const router = useRouter()
  const effectiveCategory = React.useCallback(
    (app: AppCatalogItem) => String(app.category || '').trim() || 'Uncategorized',
    []
  )

  React.useEffect(() => {
    if (isPublicMode || !authUser) return
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    if (!API_URL) return
    fetch(`${API_URL}/v1/admin/feature-requests`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (payload?.data) {
          const pending = new Set<string>(
            (payload.data as { feature_key: string; status: string }[])
              .filter((r) => r.status === 'pending')
              .map((r) => r.feature_key)
          )
          if (pending.size > 0) setRequestedKeys(pending)
        }
      })
      .catch(() => {})
  }, [isPublicMode, authUser])

  const submitAccessRequest = async () => {
    if (!requestDialogApp) return
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    if (!API_URL) return
    setRequestSubmitting(true)
    try {
      const res = await fetch(`${API_URL}/v1/admin/feature-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feature_key: requestDialogApp.feature_key || `app:${requestDialogApp.app_key}`,
          use_case: requestUseCase.trim(),
        }),
      })
      if (res.ok) {
        const data = await res.json()
        const key = requestDialogApp.feature_key || `app:${requestDialogApp.app_key}`
        setRequestedKeys((prev) => new Set(prev).add(key))
        if (data.already_requested) {
          toast({ title: 'You already have a pending request for this app.' })
        } else {
          toast({ title: 'Access request submitted!', description: 'An admin will review your request.' })
        }
        setRequestDialogApp(null)
        setRequestUseCase('')
      } else {
        toast({ title: 'Failed to submit request', variant: 'destructive' })
      }
    } catch {
      toast({ title: 'Failed to submit request', variant: 'destructive' })
    } finally {
      setRequestSubmitting(false)
    }
  }

  const isRequested = (app: AppCatalogItem) =>
    requestedKeys.has(app.feature_key || `app:${app.app_key}`)

  const loadApps = React.useCallback(
    async (opts?: { append?: boolean; offset?: number; cursor?: string | null; signal?: AbortSignal }) => {
      const append = Boolean(opts?.append)
      const API_URL = process.env.NEXT_PUBLIC_API_URL
      if (!API_URL) {
        toast({ title: 'Missing NEXT_PUBLIC_API_URL', variant: 'destructive' })
        setLoading(false)
        setIsFetching(false)
        return
      }
      const requestSeq = ++requestSeqRef.current
      try {
        const currentOffset = append ? (opts?.offset ?? 0) : 0
        const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
        const cursor = append ? (opts?.cursor ?? null) : null
        if (cursor) {
          params.set('cursor', cursor)
        } else {
          params.set('offset', String(currentOffset))
        }
        if (searchQuery.trim()) params.set('q', searchQuery.trim())
        if (selectedKind !== 'all') params.set('kind', selectedKind)
        if (selectedCategory !== 'all') params.set('category', selectedCategory)
        params.set('sort', selectedSort)
        const endpoint = isPublicMode ? `${API_URL}/v1/apps/public` : `${API_URL}/v1/apps`
        const res = await fetchWithAuth(
          `${endpoint}?${params.toString()}`,
          { credentials: 'include', headers: { Accept: 'application/json' }, signal: opts?.signal },
          undefined,
          { skipUnauthorizedRedirect: true },
        )
        if (!res.ok) {
          throw new Error(`Failed to load apps: ${res.status}`)
        }
        const payload = (await res.json()) as AppCatalogListResponse
        if (requestSeq !== requestSeqRef.current) return
        if (typeof payload.total === 'number') {
          setTotalCount(payload.total)
        } else if (!append) {
          setTotalCount(null)
        }
        setNextCursor(payload.next_cursor ?? null)
        if (append) {
          setApps((prev) => [...prev, ...(payload.data ?? [])])
        } else {
          setApps(payload.data ?? [])
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (requestSeq === requestSeqRef.current) {
          toast({ title: 'Failed to load apps catalog', variant: 'destructive' })
        }
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setLoading(false)
          setIsFetching(false)
          hasLoadedOnceRef.current = true
        }
      }
    },
    [PAGE_SIZE, searchQuery, selectedKind, selectedCategory, selectedSort, isPublicMode, toast]
  )

  const loadCategoryOptions = React.useCallback(
    async (signal?: AbortSignal) => {
      const API_URL = process.env.NEXT_PUBLIC_API_URL
      if (!API_URL) return

      try {
        const endpoint = isPublicMode ? `${API_URL}/v1/apps/public` : `${API_URL}/v1/apps`
        const discovered = new Set<string>()
        let cursor: string | null = null
        let guard = 0

        // Fetch the full category facet set for the current query/kind/sort,
        // intentionally excluding category so options don't collapse after selection.
        do {
          const params = new URLSearchParams({ limit: '100' })
          if (cursor) params.set('cursor', cursor)
          if (searchQuery.trim()) params.set('q', searchQuery.trim())
          if (selectedKind !== 'all') params.set('kind', selectedKind)
          params.set('sort', selectedSort)

          const res = await fetchWithAuth(
            `${endpoint}?${params.toString()}`,
            { credentials: 'include', headers: { Accept: 'application/json' }, signal },
            undefined,
            { skipUnauthorizedRedirect: true },
          )
          if (!res.ok) break

          const payload = (await res.json()) as AppCatalogListResponse
          for (const app of payload.data ?? []) {
            const category = effectiveCategory(app)
            if (category) discovered.add(category)
          }
          cursor = payload.next_cursor ?? null
          guard += 1
        } while (cursor && guard < 20)

        const next = Array.from(discovered).sort()
        setCategoryOptions((prev) => {
          // Preserve currently selected value in options during transitions.
          const merged = new Set<string>([...prev, ...next])
          const selected = String(selectedCategory || '').trim()
          if (selected && selected !== 'all') merged.add(selected)
          return Array.from(merged).sort()
        })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
      }
    },
    [isPublicMode, searchQuery, selectedKind, selectedSort, selectedCategory, effectiveCategory]
  )

  React.useEffect(() => {
    if (isAuthLoading) return
    const controller = new AbortController()
    if (hasLoadedOnceRef.current) {
      setIsFetching(true)
    } else {
      setLoading(true)
    }
    setNextCursor(null)
    void loadApps({ signal: controller.signal })
    return () => controller.abort()
  }, [loadApps, isAuthLoading])

  React.useEffect(() => {
    if (isAuthLoading) return
    const controller = new AbortController()
    void loadCategoryOptions(controller.signal)
    return () => controller.abort()
  }, [loadCategoryOptions, isAuthLoading])

  const displayedApps = React.useMemo(() => {
    if (selectedCategory === 'all') return apps
    return apps.filter((app) => effectiveCategory(app) === selectedCategory)
  }, [apps, selectedCategory, effectiveCategory])
  const hasMore = Boolean(nextCursor) || (typeof totalCount === 'number' && displayedApps.length < totalCount)

  React.useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!searchWrapRef.current || (target && searchWrapRef.current.contains(target))) return
      setSearchOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  const inlineResults = React.useMemo(() => displayedApps.slice(0, 8), [displayedApps])
  const featuredApps = React.useMemo(
    () =>
      FEATURED_APP_KEYS.map((key) => displayedApps.find((app) => app.app_key === key)).filter(
        (app): app is AppCatalogItem => Boolean(app)
      ),
    [displayedApps]
  )

  const canLaunch = (app: AppCatalogItem) => app.has_access

  const primaryAuthor = (app: AppCatalogItem) => {
    return app.authors && app.authors.length > 0 ? app.authors[0] : 'Unknown'
  }
  const appLastUpdatedLabel = (app: AppCatalogItem) => {
    const raw = String(app.last_updated_at || app.publication_date || '').trim()
    if (!raw) return 'Last updated —'
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return 'Last updated —'
    return `Last updated ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)}`
  }
  const appOverviewLabel = (app: AppCatalogItem) => {
    const raw = String(app.overview || effectiveCategory(app) || 'No description').trim()
    // Prefer explicit wording over AF acronym in app descriptions.
    return raw.replace(/\bAF\b/g, 'Folding models')
  }
  const launchApp = async (app: AppCatalogItem) => {
    if (!canLaunch(app)) return

    const externalUrl = EXTERNAL_APP_LINK_BY_KEY[app.app_key]
    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
      return
    }

    if (isPublicMode) {
      if (app.launch_type === 'route') {
        const modelParam = APP_KEY_TO_MODEL[app.app_key]
        const target = modelParam ? `${app.launch_target}?model=${modelParam}` : app.launch_target
        router.push(target)
      } else {
        router.push('/login')
      }
      return
    }

    const scopedTeamId = normalizeTeamId(activeTeamId)

    if (app.launch_type === 'route') {
      const modelParam = APP_KEY_TO_MODEL[app.app_key]
      const target = modelParam ? `${app.launch_target}?model=${modelParam}` : app.launch_target
      router.push(appendWorkspaceScopeToPath(target, scopedTeamId))
      return
    }

    const API_URL = process.env.NEXT_PUBLIC_API_URL
    if (!API_URL) {
      toast({ title: 'Missing NEXT_PUBLIC_API_URL', variant: 'destructive' })
      return
    }

    setLaunchingAppKey(app.app_key)
    try {
      const params = new URLSearchParams({ library_workspace: 'true' })
      if (scopedTeamId) params.set('team_id', scopedTeamId)
      const runName = `My ${app.name} Run ${new Date().toISOString().slice(0, 10)}`

      const res = await fetchWithAuth(`${API_URL}/v1/workflows/graph/add?${params.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name: runName, workflow_name: app.launch_target }),
      })
      if (!res.ok) {
        throw new Error(`Failed to create workflow: ${res.status}`)
      }

      const json = (await res.json()) as { workflow_id?: string }
      const workflowId = String(json.workflow_id || '')
      if (!workflowId) {
        throw new Error('Invalid response: missing workflow_id')
      }
      setActiveTeamId(scopedTeamId)
      router.push(
        appendWorkspaceScopeToPath(
          `/workflow/composer/${workflowId}?template=${encodeURIComponent(app.app_key)}&show_presets=true`,
          scopedTeamId
        )
      )
    } catch {
      toast({ title: `Could not launch ${app.name}`, variant: 'destructive' })
    } finally {
      setLaunchingAppKey(null)
    }
  }

  const getAppRouteTarget = (app: AppCatalogItem): string | null => {
    const externalUrl = EXTERNAL_APP_LINK_BY_KEY[app.app_key]
    if (externalUrl) return externalUrl
    if (app.launch_type !== 'route') return null
    const modelParam = APP_KEY_TO_MODEL[app.app_key]
    const target = modelParam ? `${app.launch_target}?model=${modelParam}` : app.launch_target
    if (isPublicMode) return target
    return appendWorkspaceScopeToPath(target, normalizeTeamId(activeTeamId))
  }

  const openAppInNewTab = (app: AppCatalogItem) => {
    if (!canLaunch(app)) return
    const target = getAppRouteTarget(app)
    if (!target) return
    window.open(target, '_blank', 'noopener,noreferrer')
  }

  const goToAppJobs = (app: AppCatalogItem) => {
    if (isPublicMode) {
      router.push('/login')
      return
    }
    const scopedTeamId = normalizeTeamId(activeTeamId)
    const target = `/jobs?app_key=${encodeURIComponent(app.app_key)}`
    router.push(appendWorkspaceScopeToPath(target, scopedTeamId))
  }

  const handleAppPrimaryClick = (event: React.MouseEvent<HTMLElement>, app: AppCatalogItem) => {
    if (!canLaunch(app)) return
    if ((event.metaKey || event.ctrlKey) && app.launch_type === 'route') {
      event.preventDefault()
      event.stopPropagation()
      openAppInNewTab(app)
      return
    }
    void launchApp(app)
  }

  const handleAppAuxClick = (event: React.MouseEvent<HTMLElement>, app: AppCatalogItem) => {
    if (event.button !== 1) return
    if (app.launch_type !== 'route') return
    event.preventDefault()
    event.stopPropagation()
    openAppInNewTab(app)
  }

  const renderAppListRow = (
    app: AppCatalogItem,
    opts?: { showFeatured?: boolean; isLast?: boolean; rowKey?: string }
  ) => (
    <div
      key={opts?.rowKey || app.app_key}
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 transition-colors',
        !opts?.isLast && 'border-b',
        canLaunch(app) ? 'cursor-pointer hover:bg-muted/30' : 'opacity-75'
      )}
      onClick={(event) => handleAppPrimaryClick(event, app)}
      onAuxClick={(event) => handleAppAuxClick(event, app)}
    >
      <AppAvatar app={app} sizeClass="h-8 w-8 shrink-0 rounded-md" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="truncate text-sm font-medium text-foreground">{app.name}</div>
          {opts?.showFeatured ? (
            <AppsBadge variant="secondary" className="shrink-0 text-[10px]">
              Featured
            </AppsBadge>
          ) : null}
          {NEW_APP_KEYS.has(app.app_key) ? (
            <AppsBadge className="shrink-0 bg-pink-500/15 text-[10px] text-pink-600 hover:bg-pink-500/15 dark:text-pink-400">
              New
            </AppsBadge>
          ) : null}
        </div>
        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span>{kindLabel[app.kind] || app.kind}</span>
          <span aria-hidden>·</span>
          <span className="truncate">{effectiveCategory(app)}</span>
          <span className="hidden sm:inline" aria-hidden>
            ·
          </span>
          <span className="hidden truncate sm:inline">{appLastUpdatedLabel(app)}</span>
          <span className="hidden md:inline" aria-hidden>
            ·
          </span>
          <span className="hidden items-center gap-1 truncate md:inline-flex">
            <User className="h-3 w-3 shrink-0" />
            {primaryAuthor(app)}
          </span>
          {app.github_link ? (
            <button
              type="button"
              className="hidden items-center text-muted-foreground hover:text-foreground md:inline-flex"
              aria-label="Open GitHub"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                window.open(app.github_link ?? '', '_blank', 'noopener,noreferrer')
              }}
            >
              <Github className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {app.version ? (
          <AppsBadge variant="outline" className="hidden shrink-0 text-[10px] sm:inline-flex">
            {app.version}
          </AppsBadge>
        ) : null}
        {canLaunch(app) ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="App job history"
                  onClick={(e) => {
                    e.stopPropagation()
                    goToAppJobs(app)
                  }}
                >
                  <History className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>App job history</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Use app"
                  onClick={(e) => {
                    e.stopPropagation()
                    void launchApp(app)
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Use app</TooltipContent>
            </Tooltip>
          </>
        ) : isRequested(app) ? (
          <AppsBadge variant="outline" className="shrink-0 text-[10px] text-amber-600 dark:text-amber-400">
            <Send className="mr-1 h-3 w-3" />
            Requested
          </AppsBadge>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation()
              setRequestDialogApp(app)
            }}
          >
            <Lock className="mr-1 h-3 w-3" />
            Request
          </Button>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="space-y-6 p-2 lg:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-10 w-[360px]" />
          <Skeleton className="h-10 w-[180px]" />
          <Skeleton className="h-10 w-[200px]" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
        <section className="space-y-3">
          <div className="text-sm font-medium text-foreground">Featured Apps</div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={`featured-skeleton-${idx}`} className="overflow-hidden rounded-xl border bg-background">
                <Skeleton className="h-56 w-full rounded-none" />
                <div className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-10 rounded-full" />
                    <Skeleton className="h-5 w-14 rounded-full" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                  </div>
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex justify-end gap-2 pt-1">
                    <Skeleton className="h-7 w-7 rounded-md" />
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="text-sm font-medium text-foreground">All Apps</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 9 }).map((_, idx) => (
            <div key={`app-skeleton-${idx}`} className="rounded-xl border bg-background p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-12 w-12 rounded-md" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-12 rounded-full" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Skeleton className="h-5 w-20 rounded-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-7 rounded-md" />
                  <Skeleton className="h-7 w-7 rounded-md" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const kindFilterLabel =
    KIND_FILTER_OPTIONS.find((opt) => opt.value === selectedKind)?.label || 'All Types'
  const categoryFilterLabel = selectedCategory === 'all' ? 'All Categories' : selectedCategory
  const sortFilterLabel =
    SORT_FILTER_OPTIONS.find((opt) => opt.value === selectedSort)?.label || 'Last updated'
  const activeFilterCount = [
    selectedKind !== 'all',
    selectedCategory !== 'all',
    selectedSort !== 'last_updated',
  ].filter(Boolean).length

  const renderFilterControls = (stacked: boolean) => {
    const triggerClass = stacked
      ? 'h-9 w-full justify-between gap-2'
      : 'h-9 shrink-0 gap-2 rounded-none border-0 px-3 shadow-none hover:bg-muted/40'

    const filters = (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant={stacked ? 'outline' : 'ghost'} className={cn(triggerClass, 'text-muted-foreground')}>
              <span className="inline-flex min-w-0 items-center gap-2">
                <Boxes className="h-4 w-4 shrink-0" />
                <span className="truncate">{kindFilterLabel}</span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-80" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[10rem]">
            {KIND_FILTER_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                className="gap-2"
                onSelect={() => setSelectedKind(opt.value)}
              >
                <Boxes className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1">{opt.label}</span>
                {selectedKind === opt.value ? <Check className="h-4 w-4" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant={stacked ? 'outline' : 'ghost'} className={cn(triggerClass, 'text-muted-foreground')}>
              <span className="inline-flex min-w-0 items-center gap-2">
                <Tags className="h-4 w-4 shrink-0" />
                <span className="truncate max-w-[140px]">{categoryFilterLabel}</span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-80" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[12rem]">
            <DropdownMenuItem className="gap-2" onSelect={() => setSelectedCategory('all')}>
              <Tags className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1">All Categories</span>
              {selectedCategory === 'all' ? <Check className="h-4 w-4" /> : null}
            </DropdownMenuItem>
            {categoryOptions.map((category) => (
              <DropdownMenuItem
                key={category}
                className="gap-2"
                onSelect={() => setSelectedCategory(category)}
              >
                <Tags className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate">{category}</span>
                {selectedCategory === category ? <Check className="h-4 w-4" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant={stacked ? 'outline' : 'ghost'} className={cn(triggerClass, 'text-muted-foreground')}>
              <span className="inline-flex min-w-0 items-center gap-2">
                <ArrowUpDown className="h-4 w-4 shrink-0" />
                <span className="truncate">{sortFilterLabel}</span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-80" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[11rem]">
            {SORT_FILTER_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                className="gap-2"
                onSelect={() => setSelectedSort(opt.value)}
              >
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1">{opt.label}</span>
                {selectedSort === opt.value ? <Check className="h-4 w-4" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    )

    if (stacked) return <div className="grid gap-2">{filters}</div>
    return (
      <div className="inline-flex h-9 shrink-0 items-center overflow-hidden rounded-md border border-border bg-background divide-x divide-border">
        {filters}
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={120}>
    <div className="space-y-6 p-2 lg:p-4">
      <div className="relative z-20 flex min-w-0 flex-nowrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <div ref={searchWrapRef} className="relative min-w-[140px] max-w-[280px] flex-1 sm:max-w-[320px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search apps"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onFocus={() => setSearchOpen(true)}
              className="h-9 w-full pl-10"
            />
            {searchOpen && searchQuery.trim().length > 0 ? (
              <div className="absolute z-20 mt-2 w-full rounded-md border bg-background p-1 shadow-md">
                {inlineResults.length > 0 ? (
                  <div className="max-h-72 overflow-y-auto">
                    {inlineResults.map((app) => (
                      <button
                        key={`inline-${app.app_key}`}
                        type="button"
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-muted/40"
                        onClick={(event) => {
                          setSearchOpen(false)
                          handleAppPrimaryClick(event, app)
                        }}
                        onAuxClick={(event) => handleAppAuxClick(event, app)}
                      >
                        <AppAvatar app={app} sizeClass="h-5 w-5 shrink-0 rounded-sm" />
                        <span className="truncate text-sm text-foreground">{app.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{kindLabel[app.kind] || app.kind}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No apps found.</div>
                )}
              </div>
            ) : null}
          </div>
          <div className="hidden lg:block">{renderFilterControls(false)}</div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-9 shrink-0 gap-1.5 px-2.5 lg:hidden"
                aria-label="Filters"
              >
                <ListFilter className="h-4 w-4" />
                {activeFilterCount > 0 ? (
                  <Badge variant="secondary" className="rounded-full px-1.5 py-0 font-normal">
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-3">
              {renderFilterControls(true)}
            </PopoverContent>
          </Popover>
        </div>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </div>

      {featuredApps.length > 0 ? (
        <section className="space-y-3">
          <div className="text-sm font-medium text-muted-foreground">Featured Apps</div>
          {viewMode === 'list' ? (
            <div className="overflow-hidden rounded-xl border bg-background">
              {featuredApps.map((app, idx) =>
                renderAppListRow(app, {
                  showFeatured: true,
                  isLast: idx === featuredApps.length - 1,
                  rowKey: `featured-${app.app_key}`,
                })
              )}
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-4 ${featuredApps.length >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
              {featuredApps.map((app) => {
                const coverSrc = FEATURED_COVER_BY_APP_KEY[app.app_key] ?? app.avatar_url ?? '/simbol_black.svg'
                const coverExt = String(coverSrc).toLowerCase().split('?')[0]?.split('.').pop() ?? ''
                // GIFs and already-compressed JPEGs: skip Next optimizer (double-encode makes them soft).
                const skipOptimize = coverExt === 'gif' || coverExt === 'jpg' || coverExt === 'jpeg'
                const isCoverLoaded = Boolean(loadedFeaturedCovers[coverSrc])
                return (
                  <div
                    key={`featured-${app.app_key}`}
                    role="button"
                    tabIndex={0}
                    className={`group overflow-hidden rounded-xl border bg-background text-left transition-colors ${
                      canLaunch(app) ? 'hover:bg-muted/20 cursor-pointer' : 'cursor-pointer'
                    }`}
                    onClick={(event) => handleAppPrimaryClick(event, app)}
                    onAuxClick={(event) => handleAppAuxClick(event, app)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (canLaunch(app)) void launchApp(app)
                      }
                    }}
                  >
                    <div className="relative h-56 w-full bg-muted/40">
                      <div
                        className={cn(
                          'absolute inset-0 animate-pulse bg-muted/60 transition-opacity duration-300',
                          isCoverLoaded && 'pointer-events-none opacity-0'
                        )}
                      />
                      <Image
                        src={coverSrc}
                        alt={app.name}
                        fill
                        sizes="(min-width: 1024px) 33vw, 100vw"
                        quality={100}
                        priority
                        unoptimized={skipOptimize}
                        onLoad={() =>
                          setLoadedFeaturedCovers((prev) => ({
                            ...prev,
                            [coverSrc]: true,
                          }))
                        }
                        onError={() =>
                          setLoadedFeaturedCovers((prev) => ({
                            ...prev,
                            [coverSrc]: true,
                          }))
                        }
                        className={cn(
                          'object-cover object-[50%_28%] transition-opacity duration-300',
                          isCoverLoaded ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </div>
                    <div className="space-y-2 p-4">
                      <div className="flex items-center gap-2">
                        {NEW_APP_KEYS.has(app.app_key) && (
                          <AppsBadge className="bg-pink-500/15 text-pink-600 dark:text-pink-400 text-[10px] hover:bg-pink-500/15">
                            New
                          </AppsBadge>
                        )}
                        <AppsBadge variant="secondary" className="text-[10px]">
                          Featured
                        </AppsBadge>
                        <AppsBadge variant="outline" className="text-[10px]">
                          {kindLabel[app.kind] || app.kind}
                        </AppsBadge>
                        <AppsBadge variant="outline" className="text-[10px]">
                          {effectiveCategory(app)}
                        </AppsBadge>
                      </div>
                      <div className="flex min-w-0 items-center gap-3">
                        <AppAvatar app={app} sizeClass="h-10 w-10 rounded-lg" />
                        <div className="min-w-0">
                          <div className="truncate text-base font-semibold text-foreground">{app.name}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{appLastUpdatedLabel(app)}</div>
                        </div>
                      </div>
                      <p className="line-clamp-2 text-sm text-muted-foreground">
                        {appOverviewLabel(app)}
                      </p>
                      <div className="flex items-center justify-end gap-2 pt-1">
                        {canLaunch(app) ? (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  aria-label="App job history"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    goToAppJobs(app)
                                  }}
                                >
                                  <History className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>App job history</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  aria-label="Use app"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    void launchApp(app)
                                  }}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Use app</TooltipContent>
                            </Tooltip>
                          </>
                        ) : isRequested(app) ? (
                          <AppsBadge variant="outline" className="shrink-0 text-amber-600 dark:text-amber-400">
                            <Send className="mr-1 h-3 w-3" />
                            Requested
                          </AppsBadge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              setRequestDialogApp(app)
                            }}
                          >
                            <Lock className="mr-1 h-3 w-3" />
                            Request Access
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      ) : null}

      <div className="text-sm font-medium text-muted-foreground">All Apps</div>

      {viewMode === 'list' ? (
        <div className="overflow-hidden rounded-xl border bg-background">
          {displayedApps.map((app, idx) =>
            renderAppListRow(app, { isLast: idx === displayedApps.length - 1 })
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {displayedApps.map((app) => (
            <div
              key={app.app_key}
              className={`flex h-full flex-col rounded-xl border bg-background p-4 ${canLaunch(app) ? 'hover:bg-muted/20 cursor-pointer' : 'opacity-75'} transition-colors`}
              onClick={(event) => handleAppPrimaryClick(event, app)}
              onAuxClick={(event) => handleAppAuxClick(event, app)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <AppAvatar app={app} sizeClass="h-12 w-12" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">{app.name}</div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <AppsBadge variant="outline" className="text-xs">
                        {kindLabel[app.kind] || app.kind}
                      </AppsBadge>
                      <AppsBadge variant="outline" className="text-xs">
                        {effectiveCategory(app)}
                      </AppsBadge>
                      {app.version ? (
                        <AppsBadge variant="outline" className="text-xs">
                          {app.version}
                        </AppsBadge>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-3 min-h-10 line-clamp-2 text-sm text-muted-foreground">
                {appOverviewLabel(app)}
              </p>
              <div className="mt-3 text-xs text-muted-foreground">{appLastUpdatedLabel(app)}</div>
              <div className="mt-auto pt-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <AppsBadge variant="secondary" className="text-[11px]">
                      <User className="mr-1 h-3 w-3" />
                      {primaryAuthor(app)}
                    </AppsBadge>
                    {app.github_link ? (
                      <AppsBadge
                        variant="secondary"
                        className="cursor-pointer text-[11px] hover:bg-muted"
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          window.open(app.github_link ?? '', '_blank', 'noopener,noreferrer')
                        }}
                      >
                        <Github className="h-3 w-3" />
                      </AppsBadge>
                    ) : null}
                  </div>
                  {canLaunch(app) ? (
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            aria-label="App job history"
                            onClick={(e) => {
                              e.stopPropagation()
                              goToAppJobs(app)
                            }}
                          >
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>App job history</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            aria-label="Use app"
                            onClick={(e) => {
                              e.stopPropagation()
                              void launchApp(app)
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Use app</TooltipContent>
                      </Tooltip>
                    </div>
                  ) : (
                    isRequested(app) ? (
                      <AppsBadge variant="outline" className="shrink-0 text-amber-600 dark:text-amber-400">
                        <Send className="mr-1 h-3 w-3" />
                        Requested
                      </AppsBadge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 h-7 text-xs"
                        onClick={(e) => {
                          e.stopPropagation()
                          setRequestDialogApp(app)
                        }}
                      >
                        <Lock className="mr-1 h-3 w-3" />
                        Request Access
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {displayedApps.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">No apps found for current filters.</div>
      ) : null}
      {displayedApps.length > 0 && hasMore ? (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={isFetching}
            onClick={() => {
              setIsFetching(true)
              void loadApps({ append: true, offset: displayedApps.length, cursor: nextCursor })
            }}
          >
            {isFetching ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      ) : null}

      <Dialog open={!!requestDialogApp} onOpenChange={(open) => { if (!open) { setRequestDialogApp(null); setRequestUseCase('') } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Access</DialogTitle>
          </DialogHeader>
          {requestDialogApp && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <AppAvatar app={requestDialogApp} sizeClass="h-10 w-10 rounded-lg" />
                <div>
                  <div className="font-medium">{requestDialogApp.name}</div>
                  <div className="text-xs text-muted-foreground">{effectiveCategory(requestDialogApp)}</div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="use-case">How will you use this app? (optional)</Label>
                <Textarea
                  id="use-case"
                  placeholder="Describe your use case..."
                  value={requestUseCase}
                  onChange={(e) => setRequestUseCase(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setRequestDialogApp(null); setRequestUseCase('') }}>
                  Cancel
                </Button>
                <Button onClick={submitAccessRequest} disabled={requestSubmitting}>
                  {requestSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  )
}
